import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { isKnownBrokenHero } from "../../lib/heroImage";

/**
 * RelatedCarousel — horizontal-scroll card row of same-category articles.
 * - On desktop: arrow buttons appear and scroll by one card-width.
 * - On mobile: native touch scroll with momentum + snap.
 */
export function RelatedCarousel({ items }) {
  const railRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return undefined;
    const update = () => {
      setCanLeft(rail.scrollLeft > 8);
      setCanRight(rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 8);
    };
    update();
    rail.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      rail.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [items?.length]);

  if (!items?.length) return null;

  const scrollBy = (dir) => {
    const rail = railRef.current;
    if (!rail) return;
    const card = rail.querySelector("[data-card]");
    const step = card ? card.offsetWidth + 28 : rail.clientWidth * 0.8;
    rail.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <section
      className="border-t border-[#E5E7EB] bg-[#F9FAFB] py-14 lg:py-20"
      data-testid="blog-related"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <div className="uppercase-label">More from the blog</div>
            <h2 className="font-display text-2xl sm:text-3xl text-[#0A2540] font-semibold tracking-tight mt-2">
              Keep reading
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden lg:flex items-center gap-2">
              <button
                type="button"
                onClick={() => scrollBy(-1)}
                disabled={!canLeft}
                aria-label="Scroll left"
                data-testid="related-prev"
                className="w-10 h-10 rounded-full border border-[#E5E7EB] bg-white flex items-center justify-center text-[#374151] disabled:opacity-40 hover:border-[#0A2540] transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={() => scrollBy(1)}
                disabled={!canRight}
                aria-label="Scroll right"
                data-testid="related-next"
                className="w-10 h-10 rounded-full border border-[#E5E7EB] bg-white flex items-center justify-center text-[#374151] disabled:opacity-40 hover:border-[#0A2540] transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            <Link
              to="/blog"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-[#0066FF]"
            >
              All articles <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div
          ref={railRef}
          className="flex gap-7 overflow-x-auto snap-x snap-mandatory pb-3 -mx-6 px-6 lg:mx-0 lg:px-0 scrollbar-thin"
          style={{ scrollbarWidth: "thin" }}
        >
          {items.map((r) => (
            <Link
              key={r.slug}
              to={`/blog/${r.slug}`}
              data-card
              data-testid={`related-${r.slug}`}
              className="group block shrink-0 w-[78vw] sm:w-[340px] lg:w-[300px] snap-start"
            >
              {r.hero_image && !isKnownBrokenHero(r.hero_image) ? (
                <div className="aspect-[16/10] rounded-xl overflow-hidden bg-white mb-4">
                  <img
                    src={r.hero_image}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    onError={(e) => {
                      // Hide broken image and let the gradient fallback show through.
                      e.currentTarget.parentElement.classList.add("bg-gradient-to-br", "from-[#0A2540]", "to-[#001A3D]");
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
              ) : (
                <div className="aspect-[16/10] rounded-xl bg-gradient-to-br from-[#0A2540] to-[#001A3D] mb-4" />
              )}
              <div className="text-[11px] uppercase tracking-wider text-[#0066FF] font-medium mb-1.5">
                {r.category}
              </div>
              <h3 className="font-display text-lg text-[#0A2540] font-semibold leading-tight group-hover:text-[#0066FF] transition-colors line-clamp-3">
                {r.title}
              </h3>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
